<template>
  <v-container class="main-container d-flex flex-column justify-center">
    <div class="mx-3 my-3">
      <!-- Voucher-ID -->
      <v-row no-gutters align="center" class="mb-2">
        <v-col cols="12" align="left" aria-hidden="true">
          <span class="text-label text-justify">{{ $t("voucher_text") }}</span>
        </v-col>
      </v-row>
      <v-row no-gutters align="center" class="textfield-margin">
        <v-col cols="12" align="left">
          <v-text-field
            v-model="voucherId"
            solo
            :class="{
              'input-error': voucherInvalid || (voucherId && voucherId.length < 32),
            }"
            data-test="input-voucher"
            :aria-label="`${$t('voucher_text')}`"
          ></v-text-field>
          <div class="text-error" v-if="voucherInvalid || (voucherId && voucherId.length < 32)">
            {{ $t("voucher_invalid") }}
          </div>
        </v-col>
      </v-row>
      <div class="text-center mt-3 mb-5">
        <!-- Loading-->
        <v-progress-circular
          :size="50"
          :width="7"
          :color="uiColor"
          indeterminate
          v-if="lockSubmit"
          :aria-label="$t('loading')"
          :aria-busy="lockSubmit"
          aria-live="polite"
        ></v-progress-circular>
        <!-- Redeem voucher -->
        <v-btn
          tile
          :color="uiColor"
          v-on:click="getVoucherValue()"
          v-if="!lockSubmit"
          :disabled="lockSubmit || (voucherId && voucherId.length < 32) || !voucherId"
          data-test="button-voucher"
          >{{ $t("voucher_use") }}</v-btn
        >
      </div>

      <!-- Voucher texts -->
      <v-card class="voucher-tile" tile elevation="2" v-if="voucherObtained">
        <div class="text-right pr-2 pt-2">
          <v-icon v-on:click="deleteVoucher()">$vuetify.icons.close</v-icon>
        </div>
        <v-row class="pt-2 pb-3 px-4">
          <v-col cols="7" class="pt-0 pb-0">
            <div class="text-left pb-0">
              <v-icon left color="#c8e6c9" v-if="voucherValidForExam"> $vuetify.icons.checkboxMarkedCircle </v-icon>
              <v-icon left color="#f0404f" v-if="!voucherValidForExam"> $vuetify.icons.closeCircle </v-icon>
              <span class="text-checkout-sum" v-if="voucherObtained.cert">{{ voucherObtained.cert }}</span>
            </div>
          </v-col>
          <v-col cols="5" class="pt-0 pb-0">
            <div class="text-right pb-0">
              <span class="text-checkout-sum"
                >- {{ voucherObtained.discount }}
                <span v-if="voucherObtained.discountType === 'P'">%</span>
                <span v-if="voucherObtained.discountType !== 'P'">{{ price.currency }}</span></span
              >
            </div>
          </v-col>
        </v-row>
        <v-row class="pt-2 pb-3 px-4" v-if="voucherObtained.valid.until || voucherObtained.valid.from">
          <v-col cols="12" class="pt-0 pb-0">
            {{ $t("voucher_expiry") }}: {{ voucherObtained.valid.from }} -
            {{ voucherObtained.valid.until }}
          </v-col>
        </v-row>
        <v-row class="pt-2 pb-3 px-4" v-if="voucherObtained.valid.use_from || voucherObtained.valid.use_until">
          <v-col cols="12" class="pt-0 pb-0">
            {{ $t("voucher_to_use") }}: {{ voucherObtained.valid.use_from }} -
            {{ voucherObtained.valid.use_until }}
          </v-col>
        </v-row>
        <div class="text-checkout-details px-5 pb-3" v-if="!voucherValidForExam">
          {{ $t("voucher_invalid_exam") }}
          <span
            v-if="voucherObtained.valid && (voucherObtained.valid.until || voucherObtained.valid.from) && !selectedDate"
          >
            {{ $t("voucher_date_not_selected") }}</span
          >
        </div>
      </v-card>

      <v-row no-gutters class="checkbox-margin" v-if="voucher100Percent">
        <v-col cols="12" align="left">
          <v-checkbox v-model="toAccept.voucher100Percent" :color="uiColor" data-test="checkbox-voucher">
            <span
              slot="label"
              class="text-label"
              :class="{
                'input-error--text': invalidForm && !this.toAccept.voucher100Percent,
              }"
              >{{ $t("voucher_100percent") }}</span
            >
          </v-checkbox>
          <div v-if="invalidForm && !this.toAccept.voucher100Percent" class="input-error--info-text">
            {{ $i18n.t("required_field") }}
          </div>
        </v-col>
      </v-row>

      <!-- Navigation Buttons -->
      <div>
        <v-btn
          tile
          :color="uiColor"
          class="next-button"
          :disabled="(onlyVoucher && !voucherValidForExam) || (voucherObtained != null && !voucherValidForExam)"
          v-on:click="validateForm"
          data-test="button-next"
          >{{ $t("proceed") }}</v-btn
        >
        <v-btn tile :color="uiColor" class="back-button" v-on:click="$emit('back-button-clicked')">{{
          $t("back")
        }}</v-btn>
      </div>
    </div>
  </v-container>
</template>

<script>
import { getVoucher } from "../services/PublicApiService";
import { requiredIf, sameAs } from "vuelidate/lib/validators";

export default {
  name: "Voucher",
  components: {},
  mounted() {
    if (typeof window !== "undefined") {
      let urlParams = new URLSearchParams(window.location.search);
      if (urlParams.has("voucher") && !this.voucherIdStore) {
        this.voucherId = urlParams.get("voucher");
        if (!this.lockSubmit && this.voucherId && this.voucherId.length === 32) {
          this.getVoucherValue();
        }
      }
    }
  },
  data() {
    return {
      voucherId: null,
      lockSubmit: false,
      certId: this.$store.getters["registration/certId"],
      voucherInvalid: false,
      uiColor: this.$store.getters["registration/uiColor"],
      selectedDate: this.$store.getters["registration/date"],
      examId: this.$store.getters["registration/examId"],
      invalidForm: false,
      price: this.$store.getters["registration/calculatedPrice"],
    };
  },
  validations: {
    toAccept: {
      voucher100Percent: {
        required: requiredIf(function () {
          return this.voucher100Percent;
        }),
        sameAs: sameAs(true),
      },
    },
  },
  methods: {
    getVoucherValue() {
      this.lockSubmit = true;
      getVoucher(this.voucherId).then((res) => {
        if (res && res.discount && res.discountType) {
          console.log("voucher successfully obtained: ", res);
          this.voucherObtained = res;
          this.voucherInvalid = false;
          this.lockSubmit = false;
          this.voucherIdStore = this.voucherId;
        } else {
          console.log("voucher request failed");
          this.voucherInvalid = true;
          this.voucherObtained = null;
          this.lockSubmit = false;
          this.voucherIdStore = null;
        }
      });
    },
    deleteVoucher() {
      this.voucherObtained = null;
      this.voucherIdStore = null;
    },
    validateForm() {
      if (this.voucher100Percent) {
        if (this.toAccept.voucher100Percent) {
          this.$emit("next-button-clicked");
        } else {
          this.invalidForm = true;
        }
      } else {
        this.$emit("next-button-clicked");
      }
    },
  },
  computed: {
    onlyVoucher() {
      return this.$store.getters["registration/onlyVoucher"];
    },
    voucher100Percent() {
      if (this.voucherObtained && this.voucherObtained.discount && this.voucherObtained.discountType) {
        if (this.voucherObtained.discount === 100 && this.voucherObtained.discountType === "P") {
          return true;
        }
      }
      return false;
    },
    voucherObtained: {
      get() {
        return this.$store.getters["registration/voucherObtained"];
      },
      set(value) {
        this.$store.dispatch("registration/updateVoucherObtained", value);
      },
    },
    toAccept: {
      get() {
        return this.$store.getters["registration/toAccept"];
      },
      set(value) {
        this.$store.dispatch("registration/updateToAccept", value);
      },
    },
    voucherIdStore: {
      get() {
        return this.$store.getters["registration/voucherId"];
      },
      set(value) {
        this.$store.dispatch("registration/updateVoucherId", value);
      },
    },
    voucherValidForExam() {
      try {
        var examDate = new Date(this.selectedDate);
        var untilDate;
        var fromDate;
        var today;
        if (!this.voucherObtained) {
          return false;
        }
        console.log("this.selectedDate", this.selectedDate);
        if (this.voucherObtained.valid && this.voucherObtained.valid.until) {
          if (!this.selectedDate) {
            return false;
          }
          untilDate = new Date(this.voucherObtained.valid.until);
          if (examDate > untilDate) {
            return false;
          }
        }
        if (this.voucherObtained.valid && this.voucherObtained.valid.from) {
          if (!this.selectedDate) {
            return false;
          }
          fromDate = new Date(this.voucherObtained.valid.from);
          if (examDate < fromDate) {
            return false;
          }
        }
        // TODO: klären, ob das passt
        if (this.voucherObtained.valid && this.voucherObtained.valid.use_until) {
          untilDate = new Date(this.voucherObtained.valid.use_until);
          today = new Date();
          if (today > untilDate) {
            return false;
          }
        }
        // TODO: klären, ob das passt
        if (this.voucherObtained.valid && this.voucherObtained.valid.use_from) {
          fromDate = new Date(this.voucherObtained.valid.from);
          today = new Date();
          if (today < fromDate) {
            return false;
          }
        }
        if (this.voucherObtained.valid && this.voucherObtained.valid.examId) {
          if (!this.examId) {
            return false;
          } else if (this.examId !== this.voucherObtained.valid.examId) {
            return false;
          }
        }
        if (this.onlyVoucher) {
          if (this.voucherObtained) {
            if (this.voucherObtained.cert) {
              return (
                this.voucherObtained.discountType === "P" &&
                this.voucherObtained.discount === 100 &&
                this.voucherObtained.cert.includes(this.certId)
              );
            } else {
              return this.voucherObtained.discountType === "P" && this.voucherObtained.discount === 100;
            }
          } else {
            return false;
          }
        }
        if (this.voucherObtained.cert) {
          return this.voucherObtained.cert.includes(this.certId); //TODO: klären, ob das passt
        } else {
          return true;
        }
      } catch (error) {
        console.log("error while validating voucher", error);
        return false;
      }
    },
  },
};
</script>

<style scoped>
.main-container {
  height: 100%;
}
.textfield-margin {
  margin-bottom: -14px;
}
.v-text-field.v-text-field--enclosed .v-text-field__details {
  height: 0;
  padding: 0;
  margin: 0;
}
.black--text ::v-deep label {
  font-size: 18px;
  color: rgba(0, 0, 0, 0.7);
}
.input-error ::v-deep .v-input__slot {
  box-shadow: 0px 3px 1px -2px rgba(240, 64, 79, 0.2), 0px 2px 2px 0px rgba(240, 64, 79, 0.2),
    0px 1px 5px 0px rgba(240, 64, 79, 0.2) !important;
}
.voucher-tile {
  width: 240px;
  margin-left: auto;
  margin-right: auto;
  margin-bottom: 24px;
}
@media (min-width: 450px) {
  .voucher-tile {
    width: 340px !important;
  }
}
</style>
